/**
 * Speiling av `apps/api/src/shared/telemetristier.ts`.
 *
 * Nettleseren sender sidevisninger, og adressen i nettleserlinja er den samme
 * som serveren ser: `/rsvp/<polett>` er hele innlogginga til en gjest. Reglene
 * må være de samme på begge sider, ellers er det tilfeldig hvilken av dem som
 * lekker. Testene i `telemetristier.test.ts` finnes i begge utgaver og skal gi
 * det samme svaret.
 */

/**
 * Å gjøre en adresse om til et operasjonsnavn – uten å ta med noe som peker
 * på et menneske.
 *
 * To ting løses av den samme funksjonen, og det er med vilje.
 *
 * Det ene er *gruppering*. Application Insights viser «forespørsler etter
 * navn». Sender vi `/api/weddings/9f3c…/guests` som navn, får vi én rad per
 * bryllup og en liste ingen kan lese. Vi vil ha `/api/weddings/{id}/guests`
 * med et antall bak. Den vanlige måten er å hente rutemalen fra Express, men
 * distribusjonen av Application Insights lapper ikke Express under ESM – målt,
 * ikke gjettet – så malen finnes ikke å hente. I stedet kjenner vi igjen
 * segmenter som *ser ut som* identifikatorer og bytter dem ut. Resultatet blir
 * det samme, og det virker også for adresser Express aldri fikk se, som
 * 404-ene og de faste filene.
 *
 * Det andre er *personvern*. Adressene våre bærer hemmeligheter: `/rsvp/:token`
 * er hele innlogginga til en gjest, og den som har poletten kan svare på andres
 * vegne. En slik lenke skal ikke ligge i et overvåkingsverktøy og bli med i en
 * feilrapport et halvt år senere.
 *
 * Derfor er standardsvaret å bytte ut: et segment må se ut som et *ord* for å
 * få stå. Kommer det en ny rute i morgen, blir den anonymisert uten at noen
 * husker på det.
 */

/** Uuid, slik databasen lager dem. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Rene tall: løpenumre, sidetall. */
const TALL = /^\d+$/;

/** Kvitteringsnumre og lignende: noen bokstaver og så sifre. */
const NUMMER = /^[A-Za-z]{1,6}[-_]?\d{3,}$/;

/**
 * Grensa der en streng slutter å være et ord og blir en polett.
 *
 * Målt mot begge sider. Poletter lages av `randomToken`, som er `base64url` av
 * 24 eller 32 byte – 32 og 43 tegn. Det lengste faste segmentet i rutene våre
 * er `partnerinvitasjon` på 17. Med 24 er det god klaring til begge, og ingen
 * ny rute kommer i nærheten uten at noen merker det.
 *
 * Alternativet – «lang og med et siffer i seg» – ble vurdert og forkastet:
 * en `base64url`-streng på 32 tegn er uten siffer i 0,4 % av tilfellene, og
 * 0,4 % av gjestenes svarlenker i et overvåkingsverktøy er 0,4 % for mange.
 */
const POLETTLENGDE = 24;

/**
 * Kortere identifikatorer som likevel ikke er ord.
 *
 * Betalingsreferanser som `mock_7fK2p-Qa1` er for korte til grensa over, men
 * har både siffer og skilletegn. Ordene i rutene våre har aldri siffer:
 * `onskeliste-bilder` og `glemt-passord` har bindestrek, men ikke ett tall.
 */
const KORT_IDENTIFIKATOR = /^(?=.*\d)(?=.*[-_])[A-Za-z0-9_-]{12,}$/;

/** E-post, både skrevet rett fram og prosentkodet. */
const EPOST = /@|%40/i;

/** Et filetternavn på slutten: `.pdf`, `.csv`, `.js`. */
const ETTERNAVN = /\.[A-Za-z0-9]{1,8}$/;

function serUtSomIdentifikator(segment: string): boolean {
  if (segment.length >= POLETTLENGDE) return true;
  if (UUID.test(segment)) return true;
  if (TALL.test(segment)) return true;
  if (NUMMER.test(segment)) return true;
  if (EPOST.test(segment)) return true;
  if (KORT_IDENTIFIKATOR.test(segment)) return true;
  return false;
}

function erIdentifikator(segment: string): boolean {
  if (serUtSomIdentifikator(segment)) return true;

  /**
   * Prøv en gang til uten filetternavnet.
   *
   * Kvitteringene hentes som `/kvitteringer/VD-0001.pdf`, og uten dette hadde
   * `.pdf` på slutten vært nok til at nummeret slapp forbi. Det samme gjelder
   * de bygde filene under `/assets/`, som har en innholdssum i navnet og ellers
   * ville gitt et nytt operasjonsnavn for hver eneste utrulling.
   *
   * `favicon.ico` og `manifest.json` blir stående: det er ordet foran punktumet
   * som avgjør, og de er ord.
   */
  const utenEtternavn = segment.replace(ETTERNAVN, '');
  return utenEtternavn !== segment && serUtSomIdentifikator(utenEtternavn);
}

/**
 * Bytter ut identifikatorer i en sti med `{id}`, og kaster spørrestrengen.
 *
 * Spørrestrengen ryker i sin helhet. Noen av dem er harmløse (`?periode=`),
 * men `?epost=` er det ikke, og en liste over hvilke som er greie er en liste
 * noen glemmer å utvide. Trenger vi et felt derfra senere, legger vi det på som
 * en egen egenskap vi har tatt stilling til.
 */
export function anonymiserSti(sti: string): string {
  const utenSpm = sti.split('?')[0] ?? '';
  const utenHash = utenSpm.split('#')[0] ?? '';
  if (utenHash === '') return '/';

  const anonymisert = utenHash
    .split('/')
    .map((segment) => (segment !== '' && erIdentifikator(segment) ? '{id}' : segment))
    .join('/');

  return anonymisert === '' ? '/' : anonymisert;
}

/**
 * Navnet operasjonen får i Application Insights, for eksempel
 * `GET /api/weddings/{id}/guests`.
 */
export function operasjonsnavn(metode: string, sti: string): string {
  return `${metode.toUpperCase()} ${anonymiserSti(sti)}`;
}

/** HTTP-metoden foran en adresse, slik SDK-ene navngir avhengigheter. */
const METODE_FORAN = /^([A-Z]{3,7})\s+(.*)$/;

/**
 * Anonymiserer en verdi som kan være en sti, en full adresse, eller en metode
 * med en av delene bak seg.
 *
 * Nettleser-SDK-et navngir kall til API-et `GET /api/rsvp/<polett>` og legger
 * det samme i `data`-feltet. Målt: uten dette gikk gjestens svarlenke ut i
 * klartekst hver gang RSVP-sida hentet noe. `anonymiserSti` alene bommer, fordi
 * metoden foran gjør at det første «segmentet» blir `GET /api`.
 */
export function anonymiserAdresse(verdi: string): string {
  const treff = METODE_FORAN.exec(verdi);
  if (treff) return `${treff[1]} ${anonymiserSti(treff[2] ?? '')}`;
  return anonymiserSti(verdi);
}
